import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import VideoCall from './VideoCall';
import apiService from '../services/api';

const VideoCallPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const requestId = searchParams.get('request_id');
  
  const [sessionData, setSessionData] = useState(null);
  const [petInfo, setPetInfo] = useState(null);
  const [lastSessionInfo, setLastSessionInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [joinRegistered, setJoinRegistered] = useState(false);
  const [sessionStartedAt, setSessionStartedAt] = useState(null);
  
  const trainerId = localStorage.getItem('trainerId');
  const clientId = localStorage.getItem('clientId');

  useEffect(() => {
    if (!requestId) {
      setError('No session request ID provided');
      setLoading(false);
      return;
    }

    loadSessionData();
  }, [requestId]);

  useEffect(() => {
    const registerJoin = async () => {
      try {
        const role = trainerId ? 'trainer' : 'client';
        await apiService.registerSessionJoin(requestId, role);
      } catch (err) {
        console.error('Failed to register session join:', err);
      } finally {
        setJoinRegistered(true);
      }
    };

    if (!loading && sessionData && requestId && !joinRegistered) {
      registerJoin();
    }
  }, [loading, sessionData, requestId, trainerId, joinRegistered]);

  const loadSessionData = async () => {
    try {
      setLoading(true);
      const response = await apiService.getSessionRequestDetails(requestId);
      
      if (response.Code === 200) {
        setSessionData(response.request);
        setPetInfo(response.pet_info);
        setLastSessionInfo(response.last_session);
      } else {
        setError(response.Message || 'Failed to load session data');
      }
    } catch (err) {
      console.error('Error loading session data:', err);
      setError('Failed to load session data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const fetchSessionStartTime = async () => {
      if (!requestId || !joinRegistered) return;
      
      try {
        const role = trainerId ? 'trainer' : 'client';
        const joinResponse = await apiService.registerSessionJoin(requestId, role);
        if (joinResponse.Code === 200 && joinResponse.session_started_at) {
          setSessionStartedAt(joinResponse.session_started_at);
        }
      } catch (err) {
        console.error('Failed to fetch session start time:', err);
      }
    };

    if (joinRegistered && requestId) {
      fetchSessionStartTime();
    }
  }, [requestId, joinRegistered, trainerId]);

  const handleSessionEnd = async (endData) => {
    try {
      // End the session on backend
      await apiService.endSession(requestId);
      
      // If trainer, redirect to post-session form
      if (trainerId) {
        navigate(`/post-session?request_id=${requestId}`);
      } else {
        // If client, redirect to client portal
        navigate('/client-portal');
      }
    } catch (err) {
      console.error('Error ending session:', err);
      // Still redirect even if API call fails
      if (trainerId) {
        navigate(`/post-session?request_id=${requestId}`);
      } else {
        navigate('/client-portal');
      }
    }
  };

  const handleBackToPortal = () => {
    if (trainerId) {
      navigate('/trainer');
    } else {
      navigate('/client-portal');
    }
  };

  if (loading) {
    return (
      <div style={{
        fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
        background: 'linear-gradient(135deg, #2c3e50 0%, #34495e 100%)',
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'white'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '2rem', marginBottom: '20px' }}>🔗</div>
          <div>Loading session...</div>
        </div>
      </div>
    );
  }

  if (error || !sessionData) {
    return (
      <div style={{
        fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
        background: 'linear-gradient(135deg, #2c3e50 0%, #34495e 100%)',
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'white',
        padding: '20px'
      }}>
        <div style={{
          background: 'rgba(231, 76, 60, 0.2)',
          border: '1px solid #e74c3c',
          borderRadius: '10px',
          padding: '30px',
          textAlign: 'center',
          maxWidth: '500px'
        }}>
          <div style={{ fontSize: '2rem', marginBottom: '20px' }}>❌</div>
          <h2 style={{ marginBottom: '15px' }}>Error</h2>
          <p style={{ marginBottom: '20px' }}>{error || 'Session not found'}</p>
          <button
            onClick={handleBackToPortal}
            style={{
              padding: '12px 30px',
              background: '#e74c3c',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '1rem',
              cursor: 'pointer'
            }}
          >
            Back to Portal
          </button>
        </div>
      </div>
    );
  }

  return (
    <VideoCall
      sessionId={requestId}
      trainerId={trainerId}
      clientId={clientId || sessionData.client_id}
      onSessionEnd={handleSessionEnd}
      onBackToClientPortal={handleBackToPortal}
      sessionOptions={{}}
      durationMinutes={sessionData.duration_minutes}
      petInfo={petInfo}
      lastSessionInfo={lastSessionInfo}
      sessionStartedAt={sessionStartedAt || sessionData.session_started_at}
    />
  );
};

export default VideoCallPage;


